import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Whatsapp1 from "@stackje/shared-components/cards/whatsapp/Whatsapp1";
import Whatsapp2 from "@stackje/shared-components/cards/whatsapp/Whatsapp2";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import { Label } from "@stackje/shared-components/ui/label";
import type { CommonColor } from "@stackje/common/config";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import { sampleData } from "@/components/add-card/constants";

// Zod schema for validation
const whatsappCardFormSchema = z.object({
  whatsappNumber: z.string().min(1, "WhatsApp number is required"),
  initialMessage: z.string(),
  buttonText: z.string(),
  title: z.string(),
  description: z.string(),
  type: z.string(),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
  }),
});

export type WhatsappCardData = z.infer<typeof whatsappCardFormSchema>;

interface WhatsappCardFormProps {
  initialData: WhatsappCardData;
  mode: "add" | "edit";
}

export function useWhatsappCardForm({ initialData, mode }: WhatsappCardFormProps) {
  const form = useForm<WhatsappCardData>({
    resolver: zodResolver(whatsappCardFormSchema),
    defaultValues: initialData,
  });

  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors as CommonColor);

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const whatsappNumber = watchedValues.whatsappNumber;
  const initialMessage = watchedValues.initialMessage;
  const buttonText = watchedValues.buttonText;
  const title = watchedValues.title;
  const description = watchedValues.description;
  const type = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors as CommonColor);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);

  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType);
    } else {
      const currentType = form.getValues("type");
      form.setValue("type", currentType === "whatsapp1" ? "whatsapp2" : "whatsapp1");
    }
  };

  const formContent = (
    <>
      <div className="space-y-2">
        <div className="py-2">
          <h2 className="text-lg font-semibold">WhatsApp Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="whatsappNumber">WhatsApp Number <span className="text-red-500">*</span></Label>
          <Controller
            name="whatsappNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <ClearableInput
                  id="whatsappNumber"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="e.g., +1234567890"
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="initialMessage">Initial Message</Label>
          <Controller
            name="initialMessage"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <SimpleMarkdownEditor
                  id="initialMessage"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Optional pre-filled message"
                  enabledFormats={{
                    bold: true,
                    italic: true,
                    underline: false,
                    strikethrough: true,
                  }}
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="buttonText">Button Text</Label>
          <Controller
            name="buttonText"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <ClearableInput
                  id="buttonText"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Start WhatsApp Chat"
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <ClearableInput
                  id="title"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Optional section title"
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="description">Description</Label>
          <Controller
            name="description"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <SimpleMarkdownEditor
                  id="description"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Optional description text"
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <input type="hidden" name="type" value={type} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />
      </div>
    </>
  );

  const getPreviewContent = () => {
    const previewData = isDirty || mode === "edit" ? {
      whatsappNumber,
      initialMessage,
      buttonText,
      title,
      description,
      colors
    } : {
      whatsappNumber: sampleData.whatsapp.whatsappNumber,
      initialMessage: sampleData.whatsapp.initialMessage,
      buttonText: sampleData.whatsapp.buttonText,
      title: sampleData.whatsapp.title,
      description: sampleData.whatsapp.description,
      colors
    };

    if (type === "whatsapp1") {
      return (
        <Whatsapp1
          whatsappNumber={previewData.whatsappNumber}
          initialMessage={previewData.initialMessage}
          buttonText={previewData.buttonText}
          title={previewData.title}
          description={previewData.description}
          colors={previewData.colors}
        />
      );
    } else {
      return (
        <Whatsapp2
          whatsappNumber={previewData.whatsappNumber}
          initialMessage={previewData.initialMessage}
          buttonText={previewData.buttonText}
          title={previewData.title}
          description={previewData.description}
          colors={previewData.colors}
        />
      );
    }
  };

  const previewContent = getPreviewContent();

  return {
    form,
    formContent,
    previewContent,
    toggleType,
    shuffleColors,
    isDirty,
    handleSubmit: form.handleSubmit,
    formData: {
      whatsappNumber,
      initialMessage,
      buttonText,
      title,
      description,
      type,
      colors,
    } as WhatsappCardData,
  };
}